import { createContext, useContext, useLayoutEffect } from 'react';

/** De knop Menu van de schil: de rail als schuiflade. */
export interface ShellMenu {
    open: boolean;
    toggle: () => void;
    /** Het id van de lade, voor `aria-controls`. */
    controls: string;
}

export interface ShellTopBarContextValue {
    /** `null` zolang de rail vast in beeld staat: dan is er geen knop Menu. */
    menu: ShellMenu | null;
    /** Een werkplek met een eigen balk neemt de knop Menu over zolang hij er is. */
    claim: () => () => void;
}

export const ShellTopBarContext = createContext<ShellTopBarContextValue | null>(null);

/**
 * Voor een werkplek over de volle hoogte met een eigen balk, zoals de opname.
 *
 * Bij een smal venster zet de schil een balk met "Menu" boven elk scherm. Heeft
 * het scherm zelf al een donkere balk, dan stapelen die twee: op een iPad staand
 * is dat het drukke begin van het scherm. Zolang een werkplek deze hook gebruikt,
 * laat de schil haar eigen balk weg en geeft ze de knop Menu mee; de werkplek zet
 * hem in haar balk. Verdwijnt de werkplek - weggenavigeerd, of vastgelopen - dan
 * komt de balk van de schil terug, zodat er altijd een weg naar het menu is.
 *
 * Geeft `null` als er geen knop Menu nodig is: buiten de schil, of zolang de rail
 * vast in beeld staat.
 */
export function useShellTopBar(): ShellMenu | null {
    const context = useContext(ShellTopBarContext);
    const claim = context?.claim;

    /* Vóór het tekenen: anders staat de balk van de schil één frame dubbel in beeld. */
    useLayoutEffect(() => claim?.(), [claim]);

    return context?.menu ?? null;
}
